import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

export default function Editoriale() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scroller = useRef(null);

  useEffect(() => {
    api.get("/posts").then(({ data }) => setPosts(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const scroll = (dir) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.85, 560), behavior: "smooth" });
  };

  return (
    <div data-testid="editoriale-page" className="pt-32 md:pt-44 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-widest text-darkgrey mb-6">Editoriale</p>
          <h1 className="font-heading text-5xl md:text-7xl leading-[0.95] max-w-4xl">
            Domande <span className="italic">Socratiche.</span>
          </h1>
          <p className="font-body text-lg text-darkgrey mt-8 max-w-2xl leading-relaxed">
            Un ponte con il lavoro editoriale su Instagram. Non un copia-incolla dei social: appunti, posizioni,
            domande scomode. Articoli e design slide, aggiornati di mano in mano.
          </p>
        </Reveal>
      </div>

      {loading ? (
        <p className="mt-16 px-6 md:px-12 max-w-7xl mx-auto font-mono2 text-xs uppercase tracking-widest text-darkgrey animate-pulse">Carico…</p>
      ) : posts.length === 0 ? (
        <p className="mt-16 px-6 md:px-12 max-w-7xl mx-auto font-editorial text-2xl text-darkgrey">Presto i primi contenuti.</p>
      ) : (
        <Reveal>
          <div className="relative mt-16">
            {posts.length > 1 && (
              <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between mb-4">
                <p className="font-mono2 text-[11px] uppercase tracking-widest text-darkgrey">Scorri →</p>
                <div className="flex items-center gap-2">
                  <button data-testid="gallery-prev" onClick={() => scroll(-1)} aria-label="Precedente"
                    className="border border-ink p-2.5 hover:bg-ink hover:text-greige transition-colors">
                    <ChevronLeft size={16} />
                  </button>
                  <button data-testid="gallery-next" onClick={() => scroll(1)} aria-label="Successivo"
                    className="border border-ink p-2.5 hover:bg-ink hover:text-greige transition-colors">
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}

            <div ref={scroller}
              className="flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory px-6 md:px-12 pb-2 scroll-smooth">
              {posts.map((p) => {
                const cover = (p.images && p.images[0]) || p.image;
                return (
                  <Link key={p.id} to={`/editoriale/${p.slug}`} data-testid={`post-card-${p.slug}`}
                    className="group snap-start shrink-0 w-[85vw] sm:w-[68vw] md:w-[520px] bg-greige border border-darkgrey/30 hover:border-ink transition-colors flex flex-col">
                    {cover && (
                      <div className="overflow-hidden border-b border-darkgrey/30 bg-paper relative">
                        <img src={cover} alt={p.title}
                          className="w-full h-64 object-contain group-hover:scale-[1.02] transition-transform duration-700" />
                        {p.type === "slide" && p.images && p.images.length > 1 && (
                          <span className="absolute top-3 right-3 font-mono2 text-[10px] uppercase tracking-widest bg-ink/85 text-greige px-2 py-1">
                            {p.images.length} slide
                          </span>
                        )}
                      </div>
                    )}
                    <div className="p-8 md:p-10 flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-6">
                        <span className="font-mono2 text-[11px] uppercase tracking-widest text-darkgrey">{p.tag}</span>
                        <span className="font-mono2 text-[11px] text-darkgrey">{p.date}</span>
                      </div>
                      <h2 className="font-heading text-3xl md:text-4xl leading-tight group-hover:italic transition-all flex items-start gap-2">
                        {p.title}
                        <ArrowUpRight size={20} className="mt-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      </h2>
                      {p.excerpt && <p className="font-editorial text-xl text-darkgrey mt-4 leading-relaxed">{p.excerpt}</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
}
